// src/data/immigrationGuides/englishRequirement.js

const englishRequirement = {
    slug: "english-requirement",

    title: "English Language Requirement for ILR and British Citizenship",

    seo: {
        metaTitle:
            "English Requirement for ILR & Citizenship UK",
        metaDescription:
            "The UK English language requirement for ILR and citizenship applications: approved SELT tests, CEFR levels, degree evidence and exemptions.",
        keywords: [
            "English requirement ILR",
            "English requirement citizenship UK",
            "B1 English UK settlement",
            "UK immigration English test",
            "SELT approved test list"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "English Requirement",
            url: "/english-requirement"
        }
    ],

    introduction: `
Anyone aged 18 or over applying to [settle in the UK (Indefinite Leave to Remain)](https://www.gov.uk/settle-in-the-uk)
or for British citizenship must normally demonstrate [knowledge of English](https://www.gov.uk/english-language), unless
an exemption applies.

The CEFR level required is not the same at every stage: some work visas now ask
for a higher level on entry than settlement itself requires, which surprises
applicants who assume the bar only gets harder over time. This guide sets out
the level required at each stage, what counts as valid evidence, who is exempt,
and how to book an approved test.
`,

    sections: [
        {
            heading: "CEFR levels: what's required and when",
            content: `
English ability is measured against the Common European Framework of Reference
for Languages (CEFR), from A1 (basic) up to C2 (fluent). The level required
depends on the route and the stage of application, not a single fixed standard:

- **Skilled Worker visa (initial application)**: level B2 across reading,
  writing, speaking and listening for applications made from 8 January 2026.
  Applicants extending or updating a visa they already held before that date
  are assessed against the previous B1 standard.
- **Indefinite Leave to Remain (most routes)**: level B1 (speaking and
  listening, or all four skills depending on how the requirement is met).
- **British citizenship (naturalisation)**: level B1, the same standard as
  most ILR applications - so applicants who already met B1 for settlement do
  not usually need to sit a higher-level test for citizenship, only a valid,
  in-date certificate.
- **Long residence settlement route (15 years' continuous residence)**: a
  reduced A2 minimum can apply where a college or training provider confirms
  the applicant is unlikely to reach the full standard through further study
  (this exception threshold itself is due to rise from B1 to B2 for
  applications made on or after 26 March 2027 - check GOV.UK nearer the date).

Because thresholds have been moving (the Skilled Worker jump from B1 to B2 is a
recent change), always confirm the current level for your specific route and
application date on GOV.UK before booking a test or relying on an old
certificate.
`
        },

        {
            heading: "Ways to prove English language ability",
            content: `
There are three routes to meeting the requirement:

1. **Pass an approved [Secure English Language Test (SELT)](https://www.gov.uk/guidance/prove-your-english-language-abilities-with-a-secure-english-language-test-selt)** at or above the
   required CEFR level, taken at a Home Office-approved test centre.
2. **Hold a degree taught or researched in English.** A UK degree normally
   satisfies this automatically. A degree from outside the UK must be verified
   as equivalent to a UK bachelor's degree (or higher) and as having been
   taught in English, via an assessment from Ecctis (formerly UK NARIC).
3. **Qualify for an exemption** - by nationality, age, or a long-term physical
   or mental condition (see below).

Some Skilled Worker applicants sponsored as doctors, dentists, nurses,
midwives or vets can instead rely on an English assessment already accepted by
their professional regulatory body, rather than sitting a separate SELT.
`
        },

        {
            heading: "Approved SELT providers and test format",
            content: `
Only tests taken with a Home Office-approved SELT provider count - a general
IELTS or TOEFL certificate taken for university admissions is not automatically
accepted for immigration purposes. The current approved providers include:

- **Trinity College London** - Secure English Language Tests for UKVI (GESE or
  ISE)
- **IELTS SELT Consortium** (British Council / IDP / Cambridge) - "IELTS for
  UKVI" (all four skills) or "IELTS Life Skills" (speaking and listening only)
- **LanguageCert** - International ESOL SELT, Academic SELT, or General SELT
- **Pearson** - PTE Academic UKVI or PTE Home
- **PSI Services (UK) Ltd** - Skills for English UKVI (mainly for testing
  outside the UK)

At B1 level specifically, both **IELTS Life Skills B1** and **Trinity GESE**
test speaking and listening only - there is no reading or writing component at
this level, which is why they are the most commonly used tests for ILR and
citizenship applications.

The Home Office reviews and updates the approved list periodically, and warns
that some third-party agents falsely claim official affiliation. Always book
directly through a provider's own website, and check the current approved test
and provider list on GOV.UK before paying for anything, since a test that was
approved when a friend used it is not guaranteed to still be approved now.
`
        },

        {
            heading: "Degree taught in English (Ecctis equivalency)",
            content: `
A degree can replace a SELT if it meets both conditions: the qualification is
equivalent to a UK bachelor's degree or higher, and it was taught or researched
in English.

- **UK degrees** from a recognised UK institution are normally accepted without
  further checks, even if part of the course was studied overseas.
- **Non-UK degrees** need Ecctis's "English Proficiency and Qualification
  Comparison" service (run on behalf of the Home Office via its Qualification
  and Language Service, QLS) to confirm both the degree-equivalence and the
  English-medium instruction. This costs **£210** and Ecctis aims to give an
  outcome within **20 working days**, though this restarts if they need more
  documents or if your awarding institution is slow to confirm your
  qualification is genuine - applicants relying on this route should start
  well before their visa application deadline. Note this service is only for
  non-UK qualifications; if your degree was awarded by a UK institution
  (even if you studied part of it abroad), you should not apply for it.
- Documents Ecctis requires you to upload: your final degree certificate,
  an official transcript (signed, stamped, dated, showing all subjects and
  grades), certified translations if the originals aren't in English, **a
  Medium of Instruction (MOI) letter from the awarding institution
  confirming the language(s) used during your study**, and identity
  documents. The MOI letter is a required part of the application, not an
  optional extra or a fallback if the certificate/transcript don't already
  show the teaching language.
- If your application is unsuccessful (e.g. the institution doesn't confirm
  your qualification as genuine within 20 working days, or the qualification
  wasn't taught in English), Ecctis does not issue a refund, since the fee
  covers their evaluation work regardless of outcome.
`
        },

        {
            heading: "Who is exempt",
            content: `
Some applicants do not need to prove English at all (see [GOV.UK's exemption guidance](https://www.gov.uk/english-language/exemptions)):

- **Nationals of majority English-speaking countries**, including Antigua and
  Barbuda, Australia, The Bahamas, Barbados, Belize, British Overseas
  Territories, Canada, Dominica, Grenada, Guyana, Jamaica, Malta, New Zealand,
  St Kitts and Nevis, St Lucia, St Vincent and the Grenadines, Trinidad and
  Tobago, and the USA.
- **Applicants aged 65 or over** at the date of application.
- **Applicants who have a long-term physical or mental condition** that
  prevents them from meeting the requirement. This needs a completed Home
  Office exemption form, signed by a registered doctor, along with current
  medical evidence - a self-declared difficulty with English is not enough on
  its own.
- Applicants under 18 are outside the requirement because it only applies to
  those aged 18 or over.

Exemptions must be evidenced in the application - they are not applied
automatically just because they might be true.
`
        },

        {
            heading: "Booking a test and how long results last",
            content: `
Tests must be booked directly with the provider (not a third party), and
availability varies by test centre and location - popular UK centres can book
up several weeks in advance, so applicants should not leave this until the last
minute before a visa or ILR deadline.

Each approved provider runs its own booking site and sets its own prices,
which can vary by country and test centre. As a starting point:

- **IELTS SELT Consortium (British Council / IDP / Cambridge)**: find a
  centre and book at ielts.org's test centre search. Academic and General
  Training tests are commonly priced around £255 in the UK, though local
  taxes and country pricing can change this - always confirm the fee shown
  for your chosen centre before paying.
- **Trinity College London (GESE / ISE)**: book directly through Trinity's
  own SELT booking portal. Family and settlement routes typically use the
  lower-priced GESE tests (around £160), while work and study routes
  typically use the higher ISE qualifications (around £200) - check which
  qualification your route requires before booking, since booking the wrong
  one wastes the fee.
- **Pearson (PTE Home)**: Pearson runs separate exam codes for each level -
  PTE Home A1, A2 and B1 - so you select the specific level your route needs
  rather than being assigned one automatically. Current pricing depends on
  your test country, shown on Pearson's own test centres and fees page.
- **LanguageCert**: search for an exam by level and location directly on
  LanguageCert's SELT exam search page. Two-skill (speaking and listening)
  SELTs typically start around £150-£160, with four-skill exams costing more;
  pricing varies by country and test centre. Be careful to book a SELT and not
  a general International ESOL qualification - the general version is cheaper
  but is **not** accepted for immigration applications.
- **PSI Services**: browse currently available exams for test-takers on
  PSI's own site and select the correct level for your route.
- **Ecctis (degree route)**: if you're using a non-UK degree instead of a
  test, apply through Ecctis's Qualification and Language Service (QLS)
  portal rather than a test-booking site - this is a document-review
  application, not an exam booking. The assessment fee is £210.

Prices above were checked against provider and GOV.UK sources in September
2026 and are a guide only - always confirm the current fee on the provider's
own booking page before paying, and confirm the test you are booking is on
GOV.UK's approved SELT list for your route.

Provider websites and prices do change, so treat the figures above as a
starting estimate and always confirm the current fee and available dates on
the provider's own site before paying.

For immigration purposes, a SELT certificate is generally only valid if it was
taken **within 2 years of the date of application** - an older certificate,
even at the right CEFR level, will usually not be accepted, and the test will
need to be retaken. Applicants who already proved their English to the required
standard in a previous successful visa application may not need to prove it
again in some cases - but this depends on the specific route and should be
checked rather than assumed.
`
        },
        {
            // Bridge to the exam. This page draws steady search demand for
            // English-requirement queries, but visitors researching the language
            // requirement are the same people who must also pass Life in the UK
            // - the two requirements are almost always satisfied together, and
            // the page previously offered no route to the test prep.
            heading: "The other settlement requirement: Life in the UK",
            content: `
The English language requirement is only half of the knowledge requirement for
settlement and citizenship. Most applicants must also pass the **Life in the UK
test**, a separate 24-question, 45-minute exam with a 75% pass mark, booked
through its own official booking service and sat at a registered test centre.

The two are assessed independently: passing a SELT does not exempt you from
Life in the UK, and an exemption from one does not automatically exempt you
from the other. Both certificates usually need to be in place before your
application is decided, so it is common to prepare for them at the same time.

You can [take a free Life in the UK mock exam](/mock-exams) in the same format
and timing as the real test, or work through the [study guide](/study-guide)
if you would rather revise the material first.
`
        }
    ],

    checklist: [
        "Confirm the exact CEFR level required for your route and application date",
        "Check whether a nationality, age or medical exemption applies",
        "If testing, confirm the provider and test are currently on the approved SELT list",
        "Book directly with the provider, not a third-party agent",
        "If relying on a degree, start the Ecctis equivalency check early if needed",
        "Check your SELT certificate is within 2 years of your application date",
        "Keep original certificates, transcripts and Ecctis letters ready to upload"
    ],

    // FAQ entries are driven by Search Console query data for this page rather
    // than invented: "bno visa english test exemptions" (14 impressions),
    // "can i use gcse english for citizenship", "english language requirement
    // for ilr dependent" and the b1-vs-b2 confusion all draw impressions at
    // position 48-58 with zero clicks, meaning the page ranks for the question
    // but does not visibly answer it in the result.
    faqs: [
        {
            question: "Is B1 or B2 English required — and why do I see both?",
            answer:
                "Both are currently correct, for different routes and dates, which is why the advice online looks contradictory. Settlement (ILR) and naturalisation currently require CEFR level B1. B2 became the standard for first-time applicants and switchers on major work routes (Skilled Worker, Scale-up, High Potential Individual) from 8 January 2026, assessed across reading, writing, speaking and listening. Two further points cause most of the confusion: applicants extending a Skilled Worker visa granted before 8 January 2026 under the same route can generally still rely on B1, and the settlement requirement itself is scheduled to rise from B1 to B2 for applications made from 26 March 2027. Check the requirement for your route and your application date, because a test at the wrong level does not satisfy the requirement and the fee is not refunded."
        },
        {
            question: "Can I use GCSE English for British citizenship?",
            answer:
                "No. GOV.UK states explicitly that GCSEs, A levels and National Vocational Qualifications (NVQs) cannot be used to prove knowledge of English for citizenship or settlement, even if they were studied and passed in the UK. You must instead either pass a Secure English Language Test (SELT) at B1 or above with an approved provider, or rely on an academic degree that was taught or researched in English. Sending the wrong qualification is a common reason applications are refused."
        },
        {
            question: "Do dependants need to meet the English requirement too?",
            answer:
                "Not usually for the initial visa or an extension, but generally yes at settlement. Dependants applying alongside a main work visa holder are not normally required to pass an English test to enter the UK or to extend their stay. When an adult dependant later applies for settlement in their own right, they must meet the requirement themselves - the main applicant meeting it does not cover them. The usual exemptions still apply: applicants aged 65 or over, applicants under 18, and those with a qualifying long-term physical or mental condition. Note also that settlement on several work routes (including Skilled Worker) is itself exempt, because English was already proved at the visa stage."
        },
        {
            question: "Are BNO visa holders exempt from the English test?",
            answer:
                "Only at the visa stage. You do not need to prove your knowledge of English to apply for or enter the UK on a British National (Overseas) visa. Settlement after the qualifying period is a separate application, and B1 speaking and listening is required at that point unless an exemption applies to you (for example being aged 65 or over). Importantly, the UK government confirmed in March 2026 that the higher B2 requirement introduced for other routes does not apply to the BN(O) route, which stays at B1. Treat the visa stage and the settlement stage as separate requirements rather than assuming an exemption carries forward."
        },
        {
            question: "What English level is needed for ILR?",
            answer:
                "Most Indefinite Leave to Remain applicants need level B1 on the CEFR scale. A reduced A2 minimum can apply on the 15-year long residence route where a college confirms the applicant is unlikely to reach the full standard through further study. Always check the level for your specific route on GOV.UK, as requirements can differ and change."
        },
        {
            question: "Is the English requirement the same for Skilled Worker visas and ILR?",
            answer:
                "Not necessarily. From 8 January 2026, most new Skilled Worker applications require level B2, higher than the B1 usually required for ILR and citizenship. Applicants extending a Skilled Worker visa held before that date are assessed against the earlier B1 standard. Check the current requirement for your application date before assuming your existing certificate is high enough."
        },
        {
            question: "Which English tests are accepted for UK immigration applications?",
            answer:
                "Only tests from a Home Office-approved Secure English Language Test (SELT) provider count, currently including Trinity College London, the IELTS SELT Consortium (British Council/IDP/Cambridge), LanguageCert, Pearson and PSI Services. A standard IELTS or TOEFL certificate taken for a university application is not automatically accepted. The approved list changes from time to time, so check GOV.UK's current SELT guidance before booking."
        },
        {
            question: "Can I use my degree instead of taking an English test?",
            answer:
                "Yes, if the degree is equivalent to a UK bachelor's degree or higher and was taught or researched in English. UK degrees are normally accepted directly. Degrees from outside the UK need a written equivalency confirmation from Ecctis (formerly UK NARIC), which has its own separate application process and can take several weeks."
        },
        {
            question: "Do I need to prove English again for citizenship after already doing it for ILR?",
            answer:
                "Often you do not need to retake it. GOV.UK allows a B1, B2, C1 or C2 qualification that has run out to be used for a citizenship application, provided it was the qualification accepted when you settled in the UK - and in that case it does not matter whether the test is still on the current list of recognised tests. For a settlement application the rule is narrower: an expired qualification can be used only if it is on the current list of recognised tests and was accepted for an earlier UK immigration application. Keep the original certificate either way, since you will need it as evidence."
        },
        {
            question: "Is there an age limit above which I don't need to prove English?",
            answer:
                "Yes. Applicants aged 65 or over at the date of application are exempt from the English language requirement. Applicants under 18 fall outside the requirement entirely, since it only applies to those aged 18 or over. A separate exemption exists for applicants with a long-term physical or mental condition, evidenced with a signed doctor's form."
        }
    ],

    // Exam first: GuidePage renders relatedPages[0] as the "Suggested next step"
    // banner, and Life in the UK is the requirement these visitors most often
    // still have outstanding.
    relatedPages: [
        {
            title: "Free Life in the UK Mock Exam",
            url: "/mock-exams"
        },
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "British Citizenship Guide",
            url: "/british-citizenship-guide"
        },
        {
            title: "Skilled Worker ILR Guide",
            url: "/skilled-worker-ilr"
        },
        {
            title: "Spouse Visa ILR Guide",
            url: "/spouse-visa-ilr"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - English Language Requirement",
            url: "https://www.gov.uk/english-language"
        },
        {
            title: "GOV.UK - English Language Exemptions",
            url: "https://www.gov.uk/english-language/exemptions"
        },
        {
            title: "Immigration Rules Appendix English Language",
            url: "https://www.gov.uk/guidance/immigration-rules/immigration-rules-appendix-english-language"
        },
        {
            title: "GOV.UK - Prove Your English Language Abilities With a SELT",
            url: "https://www.gov.uk/guidance/prove-your-english-language-abilities-with-a-secure-english-language-test-selt"
        },
        {
            title: "GOV.UK - Skilled Worker Visa: Knowledge of English",
            url: "https://www.gov.uk/skilled-worker-visa/knowledge-of-english"
        },
        {
            title: "IELTS - Find a Test Centre and Book",
            url: "https://ielts.org/test-centres"
        },
        {
            title: "Trinity College London - SELT Booking",
            url: "https://seltbooking.trinitycollege.co.uk/OEWeb/loadExamDtl.do"
        },
        {
            title: "Pearson PTE Home",
            url: "https://www.pearsonpte.com/pte-home"
        },
        {
            title: "LanguageCert - Search SELT Exams",
            url: "https://selt.languagecert.org/search-selt-exam"
        },
        {
            title: "PSI Services - Test Takers",
            url: "https://www.psiexams.com/en-gb/test-takers/"
        },
        {
            title: "Ecctis QLS - English Proficiency and Qualification Comparison",
            url: "https://qls.ecctis.com/english-qualification-comparison"
        },
        {
            title: "ENIC (UK NARIC) - UK Qualification Reference Statement",
            url: "https://www.enic.org.uk/individuals/uk-qualification-reference-statement"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-09-09"
    }
};

export default englishRequirement;
